import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent,
} from "react"
import {
  Search,
  X,
  Paperclip,
  Copy,
  Send,
  Loader2,
  ShieldCheck,
  Check,
  Link2,
  Mail,
  UploadCloud,
} from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { useToast } from "@/hooks/use-toast"
import { api } from "@/services/api"
import type { SecureShareConfig } from "@/services/api"
import type { ContactListItem, Communication } from "@/types"
import { useSecureShareStore } from "@/store/secureShareStore"
import { useAuthStore } from "@/store/authStore"
import { SecureInvitePanel } from "./SecureInvitePanel"

const TTL_PRESETS: { value: number; label: string }[] = [
  { value: 1, label: "1 Stunde" },
  { value: 24, label: "1 Tag" },
  { value: 168, label: "7 Tage" },
  { value: 720, label: "30 Tage" },
]

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

function ttlLabel(hours: number): string {
  return TTL_PRESETS.find((o) => o.value === hours)?.label || `${hours} Stunden`
}

export function SecureSendDialog() {
  const { open, setOpen } = useSecureShareStore()
  const { toast } = useToast()
  const user = useAuthStore((s) => s.user)

  const senderName = useMemo(() => {
    const n = [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim()
    return n || user?.email || ""
  }, [user])
  const senderEmail = user?.email || ""

  const [config, setConfig] = useState<SecureShareConfig | null>(null)

  const [text, setText] = useState("")
  const [files, setFiles] = useState<File[]>([])
  const [ttlHours, setTtlHours] = useState<number>(168)
  const [maxViews, setMaxViews] = useState<number>(1)
  const [passphrase, setPassphrase] = useState("")
  const [message, setMessage] = useState("")

  const [recipient, setRecipient] = useState("")
  const [contactSearch, setContactSearch] = useState("")
  const [contactResults, setContactResults] = useState<ContactListItem[]>([])
  const [contactEmails, setContactEmails] = useState<string[]>([])
  const [searching, setSearching] = useState(false)

  const [mode, setMode] = useState<"send" | "invite">("send")
  const [dragActive, setDragActive] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [resultUrl, setResultUrl] = useState<string | null>(null)
  const [sentTo, setSentTo] = useState<string | null>(null)

  const fileInputRef = useRef<HTMLInputElement>(null)
  const lastAutoMsg = useRef("")

  function buildDefaultMessage(views: number, hours: number): string {
    const attempts = views <= 1 ? "genau einmal" : `${views} Mal`
    const lines = [
      "Hallo,",
      "",
      `${senderName || "Jemand"} möchte Ihnen eine vertrauliche Information sicher übermitteln.`,
      "",
      "Über den Link unten sehen Sie den Inhalt. Bitte beachten Sie:",
      `• Sie können den Inhalt ${attempts} abrufen.`,
      `• Der Link ist ${ttlLabel(hours)} gültig.`,
      "• Nach dem Lesen können Sie das Secret direkt selbst löschen.",
      "",
      "Viele Grüße",
    ]
    if (senderName) lines.push(senderName)
    if (senderEmail) lines.push(senderEmail)
    return lines.join("\n")
  }

  // Reset + Config laden beim Öffnen; optional mit Prefill (aus der Schnellsuche)
  useEffect(() => {
    if (!open) return
    const prefill = useSecureShareStore.getState().prefill
    setMode(prefill?.mode === "invite" ? "invite" : "send")
    setText("")
    setFiles([])
    setPassphrase("")
    setMessage("")
    lastAutoMsg.current = ""
    setRecipient(prefill?.email || "")
    setContactSearch("")
    setContactResults([])
    setContactEmails([])
    setResultUrl(null)
    setSentTo(null)
    api
      .getSecureShareConfig()
      .then((cfg) => {
        setConfig(cfg)
        setTtlHours(cfg.defaultTtlHours)
        setMaxViews(cfg.defaultMaxViews)
      })
      .catch(() => setConfig(null))
  }, [open])

  // Standard-Nachricht (Body) erzeugen/aktualisieren, solange nicht manuell editiert
  useEffect(() => {
    if (!open) return
    if (message === lastAutoMsg.current) {
      const m = buildDefaultMessage(maxViews, ttlHours)
      if (m !== message) {
        setMessage(m)
        lastAutoMsg.current = m
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ttlHours, maxViews, senderName, senderEmail])

  // Kontaktsuche (debounced), matcht Name UND Organisation
  useEffect(() => {
    if (!open) return
    const q = contactSearch.trim()
    if (q.length < 2 || EMAIL_RE.test(q)) {
      setContactResults([])
      return
    }
    setSearching(true)
    const handle = setTimeout(async () => {
      try {
        const res = await api.getContacts({ search: q, limit: 8, isActive: true })
        setContactResults(res.items)
      } catch {
        setContactResults([])
      } finally {
        setSearching(false)
      }
    }, 300)
    return () => clearTimeout(handle)
  }, [contactSearch, open])

  const ttlOptions = useMemo(() => {
    const max = config?.maxTtlHours ?? 720
    return TTL_PRESETS.filter((o) => o.value <= max)
  }, [config])

  async function selectContact(contact: ContactListItem) {
    setContactResults([])
    setContactSearch("")
    if (contact.primaryEmail) setRecipient(contact.primaryEmail)
    try {
      const comms: Communication[] = await api.getCommunications({
        entityType: "contact",
        entityId: contact.id,
      })
      const emails = comms
        .filter(
          (c) => (c.mediumName?.toLowerCase().includes("mail") || c.value?.includes("@")) && c.value,
        )
        .map((c) => c.value)
      const unique = Array.from(new Set(emails))
      setContactEmails(unique)
      if (!contact.primaryEmail && unique.length) setRecipient(unique[0])
    } catch {
      setContactEmails(contact.primaryEmail ? [contact.primaryEmail] : [])
    }
  }

  function addFiles(picked: File[]) {
    if (!picked.length) return
    const maxCount = config?.maxFileCount ?? 5
    const maxBytes = (config?.maxFileSizeMb ?? 100) * 1024 * 1024
    const tooBig = picked.find((f) => f.size > maxBytes)
    if (tooBig) {
      toast({
        title: "Datei zu groß",
        description: `${tooBig.name} überschreitet ${config?.maxFileSizeMb ?? 100} MB.`,
        variant: "destructive",
      })
      return
    }
    setFiles((prev) => [...prev, ...picked].slice(0, maxCount))
  }

  function onPickFiles(e: ChangeEvent<HTMLInputElement>) {
    addFiles(Array.from(e.target.files ?? []))
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault()
    setDragActive(false)
    addFiles(Array.from(e.dataTransfer.files ?? []))
  }

  function removeFile(idx: number) {
    setFiles((prev) => prev.filter((_, i) => i !== idx))
  }

  function tryManualRecipient() {
    const v = contactSearch.trim()
    if (EMAIL_RE.test(v)) {
      setRecipient(v)
      setContactSearch("")
      setContactResults([])
    }
  }

  function buildFormData(): FormData | null {
    if (!text.trim() && files.length === 0) {
      toast({ title: "Kein Inhalt", description: "Geheimnis oder Datei angeben.", variant: "destructive" })
      return null
    }
    if (config?.passphraseMode === "required" && !passphrase) {
      toast({ title: "Passphrase erforderlich", variant: "destructive" })
      return null
    }
    const fd = new FormData()
    if (text.trim()) fd.append("text", text)
    fd.append("ttl_hours", String(ttlHours))
    fd.append("max_views", String(maxViews))
    if (passphrase) fd.append("passphrase", passphrase)
    if (recipient.trim()) fd.append("recipient_email", recipient.trim())
    files.forEach((f) => fd.append("files", f))
    return fd
  }

  async function handleCreate(send: boolean) {
    if (send && !EMAIL_RE.test(recipient.trim())) {
      toast({ title: "Empfänger fehlt", description: "Gültige Adresse wählen oder eingeben.", variant: "destructive" })
      return
    }
    const fd = buildFormData()
    if (!fd) return
    setSubmitting(true)
    try {
      const created = await api.createSecureSecret(fd)
      if (send) {
        await api.sendSecureSecretMail(created.id, {
          to: recipient.trim(),
          url: created.url,
          message: message.trim() || undefined,
        })
        setSentTo(recipient.trim())
        toast({ title: "Secret gesendet", description: `Link per E-Mail an ${recipient.trim()}.` })
      } else {
        toast({ title: "Link erzeugt", description: "Der One-Time-Link ist bereit." })
      }
      setResultUrl(created.url)
    } catch (err: unknown) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast({ title: "Fehlgeschlagen", description: detail || "Aktion nicht möglich.", variant: "destructive" })
    } finally {
      setSubmitting(false)
    }
  }

  function copyLink() {
    if (resultUrl) {
      navigator.clipboard.writeText(resultUrl)
      toast({ title: "Kopiert", description: "Link in der Zwischenablage." })
    }
  }

  const isEmailQuery = EMAIL_RE.test(contactSearch.trim())

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div className="text-left">
              <DialogTitle className="text-lg">Secret sicher senden</DialogTitle>
              <DialogDescription className="mt-0.5">
                Wie eine E-Mail – Empfänger wählen, Geheimnis eingeben, absenden. Wird nach dem Abruf gelöscht.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex gap-1 rounded-lg bg-muted p-1 text-sm">
          {(["send", "invite"] as const).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              className={cn(
                "flex-1 rounded-md px-3 py-1.5 font-medium transition-colors",
                mode === m ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
              )}
            >
              {m === "send" ? "Secret senden" : "Einladungslink"}
            </button>
          ))}
        </div>

        {mode === "invite" ? (
          <SecureInvitePanel />
        ) : resultUrl ? (
          <div className="space-y-4">
            <div className="rounded-lg border border-primary/40 bg-primary/5 p-4">
              {sentTo ? (
                <p className="flex items-center gap-2 text-sm font-medium">
                  <Check className="h-4 w-4 text-primary" /> Per E-Mail gesendet an {sentTo}
                </p>
              ) : (
                <p className="text-sm font-medium">One-Time-Link erzeugt</p>
              )}
              <div className="mt-2 flex gap-2">
                <Input readOnly value={resultUrl} className="font-mono text-xs" />
                <Button type="button" variant="secondary" onClick={copyLink}>
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                Link (und ggf. Passphrase) über einen sicheren Kanal teilen.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setResultUrl(null)}>Weiteres Secret</Button>
              <Button onClick={() => setOpen(false)}>Schließen</Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {/* An: */}
            <div className="flex items-start gap-3 rounded-lg border bg-background px-3 py-2">
              <span className="pt-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">An</span>
              <div className="min-w-0 flex-1">
                {recipient && (
                  <span className="mb-1 mr-2 inline-flex items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 py-1 text-sm">
                    <Mail className="h-3.5 w-3.5 text-primary" />
                    {recipient}
                    <button type="button" onClick={() => { setRecipient(""); setContactEmails([]) }}>
                      <X className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                    </button>
                  </span>
                )}
                <div className="relative">
                  <Search className="absolute left-0 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    className="border-0 pl-6 shadow-none focus-visible:ring-0"
                    placeholder={recipient ? "Andere Adresse oder Kontakt …" : "Kontakt, Organisation oder E-Mail …"}
                    value={contactSearch}
                    onChange={(e) => setContactSearch(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); tryManualRecipient() } }}
                  />
                  {searching && <Loader2 className="absolute right-0 top-2.5 h-4 w-4 animate-spin text-muted-foreground" />}
                  {isEmailQuery && (
                    <span className="absolute right-0 top-2 text-xs text-primary">↵ übernehmen</span>
                  )}
                </div>
                {contactResults.length > 0 && (
                  <div className="mt-1 max-h-44 overflow-auto rounded-md border">
                    {contactResults.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => selectContact(c)}
                        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
                      >
                        <span>
                          <span className="font-medium">{c.fullName}</span>
                          {c.primaryOrganization && (
                            <span className="text-muted-foreground"> · {c.primaryOrganization}</span>
                          )}
                        </span>
                        {c.primaryEmail && <span className="truncate text-xs text-muted-foreground">{c.primaryEmail}</span>}
                      </button>
                    ))}
                  </div>
                )}
                {contactEmails.length > 1 && (
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {contactEmails.map((em) => (
                      <button
                        key={em}
                        type="button"
                        onClick={() => setRecipient(em)}
                        className={cn(
                          "rounded-full border px-2.5 py-1 text-xs",
                          recipient === em ? "border-primary bg-primary/10" : "hover:bg-accent",
                        )}
                      >
                        {em}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Geheimnis + Drag&Drop */}
            <div>
              <Label>Geheimnis <span className="font-normal text-muted-foreground">(verschlüsselt, nur über den Link sichtbar)</span></Label>
              <Textarea
                className="mt-1 min-h-[100px] resize-none"
                placeholder="Passwort, Zugangsdaten, Notiz …"
                value={text}
                onChange={(e) => setText(e.target.value)}
              />
              <div
                onDragOver={(e) => { e.preventDefault(); setDragActive(true) }}
                onDragLeave={() => setDragActive(false)}
                onDrop={onDrop}
                onClick={() => fileInputRef.current?.click()}
                className={cn(
                  "mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-3 text-sm transition-colors",
                  dragActive ? "border-primary bg-primary/5 text-primary" : "border-muted-foreground/25 text-muted-foreground hover:border-primary/50",
                )}
              >
                <UploadCloud className="h-4 w-4" />
                Dateien hierher ziehen oder klicken zum Auswählen
              </div>
              <input ref={fileInputRef} type="file" multiple hidden onChange={onPickFiles} />
              {files.length > 0 && (
                <div className="mt-2 space-y-1">
                  {files.map((f, i) => (
                    <div key={i} className="flex items-center justify-between rounded-md bg-muted px-2.5 py-1.5 text-sm">
                      <span className="flex min-w-0 items-center gap-2 truncate">
                        <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                        <span className="truncate">{f.name}</span>
                        <span className="shrink-0 text-muted-foreground">({(f.size / 1024 / 1024).toFixed(1)} MB)</span>
                      </span>
                      <button type="button" onClick={() => removeFile(i)}><X className="h-4 w-4 text-muted-foreground" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Nachricht (E-Mail-Text) */}
            <div>
              <div className="flex items-center justify-between">
                <Label>Nachricht an den Empfänger</Label>
                <button
                  type="button"
                  onClick={() => { const m = buildDefaultMessage(maxViews, ttlHours); setMessage(m); lastAutoMsg.current = m }}
                  className="text-xs font-medium text-primary hover:underline"
                >
                  Standardtext einfügen
                </button>
              </div>
              <Textarea
                className="mt-1 min-h-[160px]"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </div>

            {/* Optionen */}
            <div className="grid gap-3 rounded-lg border bg-muted/40 p-3 sm:grid-cols-3">
              <div>
                <Label className="text-xs">Gültigkeit</Label>
                <Select value={String(ttlHours)} onValueChange={(v) => setTtlHours(Number(v))}>
                  <SelectTrigger className="mt-1 bg-background"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ttlOptions.map((o) => (
                      <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Erlaubte Abrufe</Label>
                <Input
                  className="mt-1 bg-background"
                  type="number"
                  min={1}
                  max={config?.maxViewsLimit ?? 10}
                  value={maxViews}
                  onChange={(e) => setMaxViews(Math.max(1, Math.min(config?.maxViewsLimit ?? 10, Number(e.target.value) || 1)))}
                />
              </div>
              {config?.passphraseMode !== "off" && (
                <div>
                  <Label className="text-xs">Passphrase {config?.passphraseMode === "required" ? "(Pflicht)" : "(optional)"}</Label>
                  <Input
                    className="mt-1 bg-background"
                    type="password"
                    autoComplete="new-password"
                    placeholder="separat übermitteln"
                    value={passphrase}
                    onChange={(e) => setPassphrase(e.target.value)}
                  />
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" disabled={submitting} onClick={() => handleCreate(false)}>
                <Link2 className="mr-1 h-4 w-4" /> Nur Link
              </Button>
              <Button disabled={submitting} onClick={() => handleCreate(true)}>
                {submitting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}
                Erzeugen & senden
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
